import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Button } from '@/components/ui/Button';
import { useState, useRef, useEffect } from 'react';

export function OrganizationSwitcher() {
  const { currentOrg, organizations, switchOrganization, isLoading } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Building2 className="w-4 h-4" />
        <span>Carregando...</span>
      </div>
    );
  }

  if (!currentOrg || organizations.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Building2 className="w-4 h-4" />
        <span>Nenhuma organização</span>
      </div>
    );
  }

  // Single organization - no need for switcher
  if (organizations.length === 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm">
        <Building2 className="w-4 h-4 text-accent" />
        <span className="font-medium">{currentOrg.name}</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 min-w-[200px] justify-between"
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-accent" />
          <span className="font-medium truncate">{currentOrg.name}</span>
        </div>
        <ChevronsUpDown className="w-4 h-4 text-muted-foreground" />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full min-w-[250px] bg-background border border-border rounded-sm shadow-lg z-50 max-h-[300px] overflow-y-auto">
          <div className="py-1">
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => {
                  switchOrganization(org.id);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center justify-between ${
                  org.id === currentOrg.id ? 'bg-accent/10' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{org.name}</div>
                    {org.slug && <div className="text-xs text-muted-foreground">@{org.slug}</div>}
                  </div>
                </div>
                {org.id === currentOrg.id && <Check className="w-4 h-4 text-accent" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
